import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAppSelector } from "../store/hooks";
import { apiRequest } from "../api/client";

function ProductDetail({ onOpenAuth, onOpenMenu }) {
  const { id } = useParams();
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const [detailStatus, setDetailStatus] = useState("idle"); // idle | loading | succeeded | failed
  const [detailError, setDetailError] = useState("");
  const [detailProduct, setDetailProduct] = useState(null);
  const [comparisonStatus, setComparisonStatus] = useState("idle"); // idle | loading | succeeded | failed
  const [comparisonByPlatform, setComparisonByPlatform] = useState(null); // {tesco:number|null,...} | null
  const [selectedSize, setSelectedSize] = useState("");

  const parseMoney = (value) => {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const cleaned = String(value).trim().replaceAll(",", ".").replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const parsed = Number.parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const matchesId = (p, matchId) => {
    if (!p || matchId == null) return false;
    const candidateIds = [
      p?.pid,
      p?.product_id,
      p?.sku,
      p?.id,
      p?.productId,
      p?.productID,
    ]
      .map((v) => (v == null ? "" : String(v)))
      .filter(Boolean);
    return candidateIds.includes(String(matchId));
  };

  const normalizePlatformKey = (platform) => {
    const p = String(platform || "").trim().toLowerCase();
    // Actowiz / providers may return variants like "ASDA Groceries", "Sainsbury’s", etc.
    if (!p) return "";
    if (p.includes("tesco")) return "tesco";
    if (p.includes("morris")) return "morrison";
    if (p.includes("asda")) return "asda";
    if (p.includes("sains")) return "sainsbury";
    if (p === "sainsburry") return "sainsbury";
    return "";
  };

  const getProvidersFromSearchResult = (result) => {
    const candidates = [
      result?.providers,
      result?.provider,
      result?.provider_list,
      result?.providerList,
      result?.retailers,
      result?.retailer_list,
      result?.retailerList,
      result?.items,
      result?.products,
    ];
    for (const c of candidates) {
      if (Array.isArray(c) && c.length) return c;
    }
    return [];
  };

  const scoreSearchResult = (result, { matchPid, matchSku, matchTitle } = {}) => {
    const providers = getProvidersFromSearchResult(result);
    const providersHitPid = matchPid != null && providers.some((p) => matchesId(p, matchPid));
    const providersHitSku =
      matchSku != null &&
      providers.some((p) => {
        const cand = p?.sku ?? p?.product_id ?? p?.pid ?? p?.id ?? "";
        return cand != null && String(cand) === String(matchSku);
      });

    const rTitle = String(result?.title ?? result?.product_name ?? result?.productName ?? "").trim();
    const rTitleLc = rTitle.toLowerCase();
    const qTitleLc = String(matchTitle || "").trim().toLowerCase();
    const titleHit = !!qTitleLc && !!rTitleLc && (rTitleLc.includes(qTitleLc) || qTitleLc.includes(rTitleLc));

    let score = 0;
    if (providersHitPid) score += 50;
    if (providersHitSku) score += 25;
    if (titleHit) score += 10;
    score += Math.min(10, providers.length); // small tie-breaker
    return score;
  };

  const product = useMemo(() => {
    if (!id) return null;
    if (detailProduct) return detailProduct;
    const actowizAll = [
      ...(byPlatform?.tesco || []),
      ...(byPlatform?.morrison || []),
      ...(byPlatform?.asda || []),
      ...(byPlatform?.sainsbury || []),
    ];
    const fromActowiz =
      actowizAll.find((p) => matchesId(p, id)) ||
      actowizAll.find((p) => p?.sku != null && String(p.sku) === String(id));
    if (fromActowiz) return fromActowiz;
    return null;
  }, [id, byPlatform, detailProduct]);

  useEffect(() => {
    // Fetch cross-retailer comparison via search providers (dynamic "Where to buy").
    // Note: byPlatform lists are retailer-specific; they rarely contain the same SKU across stores.
    if (!id) return;
    const query = String(product?.sku || product?.title || "").trim();
    if (!query) {
      setComparisonStatus("idle");
      setComparisonByPlatform(null);
      return;
    }

    let alive = true;
    const controller = new AbortController();
    setComparisonStatus("loading");
    setComparisonByPlatform(null);

    const qs = new URLSearchParams({
      query,
      start: "0",
      offset: "20",
    });

    apiRequest(`api/actowiz/search?${qs.toString()}`, { signal: controller.signal })
      .then((res) => {
        if (!alive) return;
        if (!res.ok) {
          setComparisonStatus("failed");
          return;
        }

        const results = Array.isArray(res.data?.results) ? res.data.results : [];
        // Pick best hit with a robust score (schema differences across API responses).
        const scored = results
          .map((r) => ({
            r,
            score: scoreSearchResult(r, {
              matchPid: id,
              matchSku: product?.sku,
              matchTitle: product?.title || query,
            }),
          }))
          .sort((a, b) => b.score - a.score);

        const hit = scored.length ? scored[0].r : null;
        const providers = getProvidersFromSearchResult(hit);
        if (!providers.length) {
          setComparisonStatus("succeeded");
          setComparisonByPlatform(null);
          return;
        }

        const byP = { tesco: null, morrison: null, asda: null, sainsbury: null };
        providers.forEach((p) => {
          const platformKey = normalizePlatformKey(p?.platform);
          if (!platformKey) return;
          const price = parseMoney(p?.price);
          if (!Number.isFinite(price)) return;
          const prev = byP[platformKey];
          if (!Number.isFinite(prev) || price < prev) byP[platformKey] = price;
        });

        setComparisonByPlatform(byP);
        setComparisonStatus("succeeded");
      })
      .catch((err) => {
        if (!alive) return;
        if (err?.name === "AbortError") return;
        setComparisonStatus("failed");
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [id, product?.sku, product?.title]);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    const controller = new AbortController();
    setDetailStatus("loading");
    setDetailError("");
    setDetailProduct(null);

    apiRequest(`api/actowiz/product_detail?pid=${encodeURIComponent(id)}`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!alive) return;
        if (!res.ok) {
          setDetailStatus("failed");
          setDetailError("Failed to load product details.");
          return;
        }
        const raw = Array.isArray(res.data) ? res.data[0] : res.data;
        if (!raw) {
          setDetailStatus("failed");
          setDetailError("Product details not found.");
          return;
        }
        const normalized = {
          pid: raw.product_id ?? raw.pid ?? id,
          platform: raw.platform ?? raw.retailer ?? "",
          sku: raw.sku ?? "",
          title: raw.product_name ?? raw.title ?? raw.sku ?? "",
          packsize: raw.pack_size ?? raw.packsize ?? "",
          price: parseMoney(raw.price),
          unit_price: parseMoney(raw.unit_price ?? raw.price_per_unit),
          description: raw.description ?? "",
          average_rating: raw.average_rating ?? null,
          review_count: raw.review_count ?? raw.rating_count ?? null,
          images: Array.isArray(raw.all_images)
            ? raw.all_images
            : raw.main_image_url
              ? [raw.main_image_url]
              : [],
          main_image_url: raw.main_image_url ?? "",
          url: raw.url ?? "",
          offers: raw.offers ?? null,
          nutrition: raw.nutrition ?? null,
          breadcrumbs: raw.breadcrumbs ?? null,
          features: raw.features ?? null,
        };
        setDetailProduct(normalized);
        setDetailStatus("succeeded");
      })
      .catch((err) => {
        if (!alive) return;
        // Expected on unmount / StrictMode double-invoke
        if (err?.name === "AbortError") return;
        setDetailStatus("failed");
        setDetailError("Failed to load product details.");
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [id]);

  const retailerStack = useMemo(() => {
    const platforms = ["tesco", "morrison", "asda", "sainsbury"];
    const labelByPlatform = {
      tesco: { label: "Tesco", color: "#1359EF" },
      morrison: { label: "Morrisons", color: "#A20886" },
      asda: { label: "Asda", color: "#08A237" },
      sainsbury: { label: "Sainsbury's", color: "#EC570D" },
    };

    const match = (p) => matchesId(p, id);
    // Derive current retailer key as robustly as possible so "current price" can be shown even
    // when cross-store match isn't available.
    const rawPlatform = product?.platform || product?.retailer || product?.provider || "";
    const currentPlatformKey = (() => {
      const direct =
        normalizePlatformKey(rawPlatform) ||
        (platforms.includes(String(rawPlatform || "").trim().toLowerCase())
          ? String(rawPlatform || "").trim().toLowerCase()
          : "");
      if (direct) return direct;

      // If API didn't provide a platform but we came from a specific retailer list, infer it.
      for (const k of platforms) {
        const list = byPlatform?.[k] || [];
        if (Array.isArray(list) && list.some(match)) return k;
      }
      return "";
    })();

    const rows = platforms.map((platform) => {
      const list = byPlatform?.[platform] || [];
      const found = Array.isArray(list) ? list.find(match) : null;
      const raw =
        found?.price ??
        found?.unit_price ??
        found?.price_per_unit ??
        found?.pricePerUnit ??
        found?.offer_price ??
        found?.offerPrice ??
        null;
      const price = parseMoney(raw);
      const comparisonPrice =
        comparisonByPlatform && Object.prototype.hasOwnProperty.call(comparisonByPlatform, platform)
          ? comparisonByPlatform[platform]
          : null;
      let finalPrice = comparisonPrice != null ? comparisonPrice : price;
      // If the store lists aren't loaded (or don't contain this pid), at least show the current product's retailer price.
      if (finalPrice == null && currentPlatformKey && platform === currentPlatformKey) {
        finalPrice = parseMoney(
          product?.price ?? product?.unit_price ?? product?.price_per_unit ?? product?.pricePerUnit
        );
      }
      return {
        platform,
        ...labelByPlatform[platform],
        price: finalPrice,
        formatted: finalPrice != null ? `£${finalPrice.toFixed(2)}` : "N/A",
        product: found,
      };
    });

    const prices = rows.map((r) => r.price).filter((p) => typeof p === "number" && Number.isFinite(p));
    const cheapest = prices.length ? Math.min(...prices) : null;
    const highest = prices.length ? Math.max(...prices) : null;
    const savings = cheapest != null && highest != null ? highest - cheapest : null;
    const cheapestRow = cheapest != null ? rows.find((r) => r.price === cheapest) : null;

    return {
      rows,
      cheapestRow,
      savings: savings != null && savings > 0 ? savings : null,
    };
  }, [byPlatform, id, comparisonByPlatform, product?.platform, product?.price]);

  const heroPrice = useMemo(() => {
    if (retailerStack.cheapestRow?.price != null) return retailerStack.cheapestRow.price;
    return product?.price ?? null;
  }, [retailerStack.cheapestRow?.price, product?.price]);

  const heroPriceLabel = retailerStack.cheapestRow?.label || product?.platform || "—";

  const images = product?.images || [];
  const mainImage = images[0];
  const ratingAvg = product?.average_rating;
  const ratingCount = product?.review_count;
  const ratingParsed =
    ratingAvg == null
      ? null
      : Number.parseFloat(String(ratingAvg).trim().replace(",", "."));
  const reviewCountParsed = Number.parseInt(String(ratingCount), 10);
  const ratingStars =
    typeof ratingParsed === "number" && Number.isFinite(ratingParsed)
      ? ratingParsed.toFixed(1)
      : null;

  const sizeOptions = useMemo(() => {
    const raw = String(product?.packsize || "").trim();
    const list = raw
      ? raw
          .split(/[,/|]/g)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    return list.length ? list.slice(0, 6) : ["—"];
  }, [product?.packsize]);

  useEffect(() => {
    if (!sizeOptions.length) return;
    setSelectedSize((prev) => (prev && sizeOptions.includes(prev) ? prev : sizeOptions[0] || ""));
  }, [id, sizeOptions]);

  const maxComparisonPrice = useMemo(() => {
    const rows = retailerStack?.rows || [];
    return rows.reduce((max, r) => {
      const v = r?.price;
      return typeof v === "number" && Number.isFinite(v) ? Math.max(max, v) : max;
    }, 0);
  }, [retailerStack?.rows]);

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        {detailStatus === "loading" && (
          <div className="mt-20 rounded-2xl border border-slate-200 bg-white p-5 text-center text-sm text-[#6E7B86] shadow-sm">
            Loading product details…
          </div>
        )}

        {detailStatus === "failed" && (
          <div className="mt-20 rounded-2xl border border-red-200 bg-red-50 p-5 text-center text-sm text-red-700 shadow-sm">
            {detailError || "Failed to load product details."}
          </div>
        )}

        {detailStatus !== "loading" && !product && (
          <div className="mt-20 rounded-2xl border border-slate-200 bg-white p-5 text-center text-sm text-[#6E7B86] shadow-sm">
            Product not found.
          </div>
        )}

        <div className="grid gap-8 md:grid-cols-[280px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={product?.title || product?.sku}
                  className="max-h-64 object-contain"
                />
              ) : (
                <div className="flex h-64 w-full items-center justify-center rounded-xl bg-[#F5F7FB] text-xs text-[#6E7B86]">
                  No Image
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {images.slice(0, 4).map((img, idx) => (
                <div
                  key={idx}
                  className="flex h-16 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm"
                >
                  <img
                    src={img}
                    alt={`${product?.title || product?.sku} ${idx + 1}`}
                    className="h-12 object-contain"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-extrabold text-[#24303A]">
                  {product?.title || product?.sku || "N/A"}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="rounded-full bg-slate-200 px-3 py-1 font-semibold text-[#24303A]">
                    {product?.packsize || "—"}
                  </span>
                  {(ratingStars != null || Number.isFinite(reviewCountParsed)) && (
                    <span className="rounded-full bg-[#7352C7]/10 px-3 py-1 font-semibold text-[#7352C7]">
                      {ratingStars != null ? `★ ${ratingStars}` : "★"}{" "}
                      {Number.isFinite(reviewCountParsed) ? `${reviewCountParsed} reviews` : ""}
                    </span>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-right text-xs border border-emerald-100">
                <p className="text-[11px] font-semibold text-emerald-700">
                  Cheapest at{" "}
                  <span className="text-[#24303A]">
                    {heroPriceLabel}
                  </span>
                </p>
                <p className="mt-1 text-2xl font-extrabold text-emerald-800">
                  {heroPrice != null ? `£${heroPrice.toFixed(2)}` : "£—"}
                </p>
                {retailerStack.savings != null && (
                  <p className="mt-1 text-[11px] font-semibold text-emerald-700">
                    Save £{retailerStack.savings.toFixed(2)} vs highest
                  </p>
                )}
              </div>
            </div>

            <section className="space-y-3">
              <div className="flex items-end justify-between gap-3">
                <h2 className="text-sm font-extrabold text-[#24303A]">
                Where To Buy
                </h2>
                {retailerStack.savings != null && (
                  <div className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-3 py-1 border border-emerald-100">
                    You could save up to £{retailerStack.savings.toFixed(2)}
                  </div>
                )}
              </div>

           

              <div className="space-y-2">
                  {retailerStack.rows.map((row) => (
                    <RetailRow
                      key={row.platform}
                      row={row}
                      isBest={
                        retailerStack.cheapestRow != null &&
                        row.platform === retailerStack.cheapestRow.platform
                      }
                    />
                  ))}
              </div>
            </section>

            <section className="space-y-2 text-sm">
              <h2 className="text-sm font-extrabold text-[#24303A]">
                Good to know
              </h2>
              <p className="text-xs text-[#6E7B86]">
                {product?.description
                  ? product.description.slice(0, 220)
                  : "Product description is not available for this item yet."}
              </p>
            </section>

            <section className="space-y-2">
              <h2 className="text-sm font-extrabold text-[#24303A]">Sizes</h2>
              <div className="space-y-2">
                {sizeOptions.map((size) => {
                  const isActive = size === selectedSize;
                  return (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setSelectedSize(size)}
                      className={[
                        "w-full rounded-2xl border p-3 text-left transition",
                        isActive
                          ? "border-[#7352C7] bg-[#7352C7]/5"
                          : "border-slate-200 bg-slate-50 hover:bg-white",
                      ].join(" ")}
                    >
                      <div className="grid gap-3 md:grid-cols-[260px_minmax(0,1fr)] md:items-center">
                        <div className="flex items-center gap-3">
                          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                            {mainImage ? (
                              <img
                                src={mainImage}
                                alt={product?.title || product?.sku}
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <div className="text-[10px] font-semibold text-slate-400">
                                No image
                              </div>
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={[
                                  "rounded-full px-3 py-1 text-[11px] font-semibold",
                                  isActive
                                    ? "bg-[#7352C7] text-white"
                                    : "border border-slate-200 bg-white text-slate-700",
                                ].join(" ")}
                              >
                                {size}
                              </span>
                              <span className="text-xs font-extrabold text-slate-900">
                                {heroPrice != null ? `£${heroPrice.toFixed(2)}` : "£—"}
                              </span>
                            </div>
                            <div className="mt-1 text-[11px] font-semibold text-slate-500">
                              Compare across stores
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          {(() => {
                            const baseRows = retailerStack?.rows || [];
                            const rows = baseRows.map((r) => {
                              const p = r?.platform;
                              const override =
                                comparisonByPlatform &&
                                p &&
                                Object.prototype.hasOwnProperty.call(comparisonByPlatform, p)
                                  ? comparisonByPlatform[p]
                                  : null;
                              const finalPrice =
                                typeof override === "number" && Number.isFinite(override)
                                  ? override
                                  : r?.price;
                              return { ...r, price: finalPrice };
                            });
                            const maxPrice = rows.reduce((max, r) => {
                              const v = r?.price;
                              return typeof v === "number" && Number.isFinite(v) ? Math.max(max, v) : max;
                            }, 0);

                            return (
                              <div className="w-[160px]">
                                <div className="flex items-end justify-between gap-1">
                                  {rows.map((row) => (
                                    <div key={`${size}-${row.platform}`} className="flex-1">
                                      <div
                                        className="mx-auto w-full rounded-t-md"
                                        style={{
                                          backgroundColor: row?.color || "#94A3B8",
                                          height:
                                            row.price == null || maxPrice <= 0
                                              ? 10
                                              : Math.max(10, Math.round((row.price / maxPrice) * 22)),
                                          opacity: row?.price == null ? 0.25 : 1,
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-1 grid grid-cols-4 gap-1 text-[9px] font-semibold text-slate-600">
                                  {rows.map((row) => (
                                    <div key={`${size}-${row.platform}-lbl`} className="truncate text-center">
                                      {row?.label || row?.platform}
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-0.5 grid grid-cols-4 gap-1 text-[9px] font-extrabold text-slate-900">
                                  {rows.map((row) => (
                                    <div key={`${size}-${row.platform}-pr`} className="text-center">
                                      {row?.price != null ? `£${row.price.toFixed(2)}` : "N/A"}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function RetailRow({ row, isBest }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2">
      <div className="flex min-w-0 items-center gap-3">
        <div
          className="h-8 w-8 shrink-0 rounded-md"
          style={{ backgroundColor: row?.color || "#E2E8F0" }}
        />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-xs font-semibold text-slate-900">
              {row?.label || "Store"}
            </span>
            {isBest && (
              <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                Best
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs">
        <span className="font-semibold text-slate-900">
          {row?.price != null ? `£${row.price.toFixed(2)}` : "N/A"}
        </span>
        <button
          type="button"
          className="rounded-full bg-[#7352C7] px-3 py-1 text-[11px] font-semibold text-white"
          onClick={(e) => {
            e.stopPropagation();
            // Design-only: add-to-list wiring lives elsewhere.
          }}
        >
          Add to List
        </button>
      </div>
    </div>
  );
}

export default ProductDetail;

